/**
 * Attorney Demand Letter Generator
 * 
 * Generates professional demand letters with:
 * - Multiple letter types (initial, follow-up, bad faith notice, final, appraisal)
 * - State-specific statutory references
 * - Auto-populated from case data
 * - Real-time preview
 * - Export options (text, copy)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseGenerationBlock, attestationBlockLine, responseErrorMessage } from '../services/generationGate';
import LetterheadGateNotice from '../components/attorney/LetterheadGateNotice';
import {
  ArrowLeft, FileText, Send, AlertTriangle, Clock,
  Gavel, Copy, Download, Check, Loader2, ChevronDown
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';
import { getAuthHeaders } from '../lib/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface LetterType {
  type: string;
  name: string;
  description: string;
}

const LETTER_TYPES: LetterType[] = [
  { type: 'initial_demand', name: 'Initial Demand Letter', description: 'First formal demand for payment' },
  { type: 'follow_up', name: 'Follow-Up Demand', description: 'Second demand after deadline passes' },
  { type: 'bad_faith_notice', name: 'Bad Faith Notice', description: 'Statutory notice of bad faith conduct' },
  { type: 'final_demand', name: 'Final Demand Before Litigation', description: 'Last chance before lawsuit' },
  { type: 'appraisal_demand', name: 'Appraisal Demand', description: 'Invoke appraisal clause' },
];

// Map frontend letter types to backend template types
const TEMPLATE_TYPE_MAP: Record<string, string> = {
  'initial_demand': 'initial',
  'follow_up': 'follow_up',
  'bad_faith_notice': 'bad_faith',
  'final_demand': 'final',
  'appraisal_demand': 'appraisal',
};

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

interface DemandLetterGeneratorProps {
  /** Explicit case id (e.g. from an embedding page's case picker). Falls back
   * to the `:caseId` route param when omitted, so this component behaves
   * identically at its own route (`/attorney/case/:caseId/demand-letter`). */
  caseId?: string;
}

export default function DemandLetterGenerator({ caseId: propCaseId }: DemandLetterGeneratorProps = {}) {
  const { caseId: paramCaseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const caseId = propCaseId ?? paramCaseId;
  
  // Form state
  const [letterType, setLetterType] = useState('initial_demand');
  const [responseDeadlineDays, setResponseDeadlineDays] = useState(30);
  const [includeCalculations, setIncludeCalculations] = useState(true);
  const [includeBadFaithWarning, setIncludeBadFaithWarning] = useState(true);
  
  // Case data form
  const [caseData, setCaseData] = useState({
    insured_name: '',
    business_name: '',
    insured_address: '',
    carrier_name: '',
    carrier_address: '',
    policy_number: '',
    claim_number: '',
    adjuster_name: '',
    adjuster_email: '',
    event_type: 'fire',
    event_date: '',
    claim_amount: 0,
    loss_period_days: 30,
    state: '',
    attorney_name: '',
    attorney_firm: '',
    attorney_address: '',
    attorney_phone: '',
    attorney_email: '',
    attorney_bar_number: '',
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingCase, setLoadingCase] = useState(true);
  const [generatedLetter, setGeneratedLetter] = useState<string | null>(null);
  const [reviewSections, setReviewSections] = useState<any[]>([]);
  const [stateStatutes, setStateStatutes] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [blockingPlaceholders, setBlockingPlaceholders] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Load case data on mount
  useEffect(() => {
    const fetchCaseData = async () => {
      if (!caseId) {
        setLoadingCase(false);
        return;
      }

      const authHeaders = await getAuthHeaders();
      if (!authHeaders.Authorization) {
        navigate('/attorney/login');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
          headers: authHeaders
        });

        if (response.status === 401) {
          navigate('/attorney/login');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          // Pre-fill form with case data
          setCaseData(prev => ({
            ...prev,
            insured_name: data.client_name || '',
            business_name: data.business_name || data.client_name || '',
            // Carrier and policy number live under `policy_coverage` on
            // CaseDetailResponse — the flat `data.carrier`/`data.policy_number`
            // read here never existed on the model, so both fields rendered
            // blank on a case whose policy was fully parsed.
            carrier_name: data.policy_coverage?.insurer_name || data.carrier || '',
            claim_number: data.claim_number || '',
            policy_number: data.policy_coverage?.policy_number || data.policy_number || '',
            claim_amount: data.total_demand || 0,
            event_type: data.event_type || 'fire',
            // CaseDetailResponse (backend/app/api/v1/attorney.py) exposes
            // `event_date`, not `loss_date` — that field doesn't exist on the
            // response model (FastAPI's response_model strips it), so this
            // always rendered blank. `state` isn't on the model either; drop
            // the hardcoded 'TX' fallback (it silently showed Texas statutory
            // references for every case) and let the /context fetch below
            // supply the real value.
            event_date: data.event_date || '',
            state: data.state || '',
          }));
        }
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoadingCase(false);
      }
    };

    fetchCaseData();
  }, [caseId, navigate]);

  // Fetch case context for additional auto-population
  useEffect(() => {
    if (!caseId) return;
    getAuthHeaders().then(authHeaders => fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/context`, {
      headers: authHeaders
    }))
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setCaseData(prev => ({
            ...prev,
            // Only set from context if the field is still at its default value.
            // The /context endpoint returns `event_start_date`, not `event_date`.
            event_date: prev.event_date || data.event_start_date || '',
            claim_number: prev.claim_number || data.claim_number || '',
            carrier_name: prev.carrier_name || data.carrier || '',
            insured_name: prev.insured_name || data.client_name || '',
            business_name: prev.business_name || data.business_name || '',
            // policy_number/state were previously never merged from context here
            // (unlike the other generator pages), so they stayed permanently
            // blank / stuck on the bogus 'TX' default above.
            policy_number: prev.policy_number || data.policy_number || '',
            state: prev.state || data.state || '',
            claim_amount: prev.claim_amount || data.claim_amount || 0,
          }));
        }
      })
      .catch(err => console.error('Failed to load case context:', err));
  }, [caseId]);
  
  // Load state statutes when state changes
  useEffect(() => {
    const fetchStateStatutes = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/state-statutes/${caseData.state}`);
        if (response.ok) {
          const data = await response.json();
          setStateStatutes(data.statutes);
        }
      } catch (err) {
        console.error('Failed to load state statutes:', err);
      }
    };
    
    if (caseData.state) {
      fetchStateStatutes();
    }
  }, [caseData.state]);
  
  const handleInputChange = (field: string, value: string | number) => {
    setCaseData(prev => ({ ...prev, [field]: value }));
  };
  
  const generateLetter = async () => {
    setLoading(true);
    setError('');
    setBlockingPlaceholders([]);
    setGeneratedLetter(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });

      if (!response.ok) {
        // The provenance-gate 412 returns a structured detail object; parse it so the
        // real message + blocking placeholders surface (not "[object Object]"), and so a
        // letterhead gap can deep-link to /attorney/letterhead.
        const body = await response.json().catch(() => ({}));
        const block = parseGenerationBlock(body, 'Failed to generate letter');
        setError(block.kind === 'not_attested' ? attestationBlockLine(block) : block.message);
        setBlockingPlaceholders(block.blockingPlaceholders);
        return;
      }

      const data = await response.json();
      setGeneratedLetter(data.formatted_letter);
      setReviewSections(data.review_sections || []);
      setStateStatutes(data.state_statutes);
    } catch (err: any) {
      setError(err.message || 'Failed to generate letter');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = useCallback(() => {
    if (generatedLetter) {
      navigator.clipboard.writeText(generatedLetter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [generatedLetter]);
  
  const downloadAsText = useCallback(() => {
    if (generatedLetter) {
      const blob = new Blob([generatedLetter], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [generatedLetter, caseData.claim_number]);

  const downloadAsPdf = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });
      
      if (!response.ok) throw new Error(await responseErrorMessage(response, 'Failed to generate PDF'));
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      setError((err as Error)?.message || 'Failed to download PDF');
    }
  }, [letterType, caseData, includeCalculations, includeBadFaithWarning, responseDeadlineDays]);

  const downloadAsDocx = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/demand-letter/export/docx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          letter_type: letterType,
          case_data: caseData,
          include_calculations: includeCalculations,
          include_bad_faith_warning: includeBadFaithWarning,
          response_deadline_days: responseDeadlineDays,
          output_format: 'text'
        })
      });
      
      if (!response.ok) throw new Error(await responseErrorMessage(response, 'Failed to generate DOCX'));
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demand_letter_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('DOCX download failed:', err);
      setError((err as Error)?.message || 'Failed to download DOCX');
    }
  }, [letterType, caseData, includeCalculations, includeBadFaithWarning, responseDeadlineDays]);

  const downloadTemplateDocx = useCallback(async () => {
    if (!caseId) {
      setError('Case ID required for template download');
      return;
    }
    try {
      const templateType = TEMPLATE_TYPE_MAP[letterType] || letterType;
      const response = await fetch(`${BACKEND_URL}/api/v1/reports/${caseId}/demand-letter/${templateType}`, {
        headers: await getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Failed to generate Template DOCX');

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `demand_letter_template_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.docx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Template DOCX download failed:', err);
      setError('Failed to download Template DOCX');
    }
  }, [caseId, letterType, caseData.claim_number]);

  const getLetterTypeIcon = (type: string) => {
    switch (type) {
      case 'initial_demand': return <FileText className="w-5 h-5" />;
      case 'follow_up': return <Send className="w-5 h-5" />;
      case 'bad_faith_notice': return <AlertTriangle className="w-5 h-5" />;
      case 'final_demand': return <Gavel className="w-5 h-5" />;
      case 'appraisal_demand': return <Clock className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  if (loadingCase) {
    return (
      <AttorneyLayout>
        <div className="flex items-center justify-center min-h-[60vh]" style={{ background: 'var(--color-bg-base)' }}>
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} />
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout>
      {/* Back button */}
      {caseId && (
        <button
          onClick={() => navigate(`/attorney/case/${caseId}`)}
          className="cs-body flex items-center gap-2 mb-6"
          style={{ color: 'var(--color-text-muted)' }}
          data-testid="back-to-case"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Case
        </button>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="cs-display flex items-center gap-3">
          <span className="flex-shrink-0 inline-flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 11, padding: 8 }}>
            <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
          </span>
          Demand Letter Generator
        </h1>
        <p className="cs-meta mt-1">Generate professional demand letters with state-specific statutory references</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Form */}
        <div className="space-y-6">
          {/* Letter Type Selection */}
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-4">Letter Type</h3>
            <div className="space-y-2">
              {LETTER_TYPES.map((lt) => (
                <label
                  key={lt.type}
                  className="flex items-center gap-3 p-3 cursor-pointer transition-colors"
                  style={{
                    borderRadius: 10,
                    border: '1px solid',
                    borderColor: letterType === lt.type ? 'var(--color-needs-human-tint-bd)' : 'var(--color-border)',
                    background: letterType === lt.type ? 'var(--color-needs-human-tint)' : 'transparent',
                  }}
                  data-testid={`letter-type-${lt.type}`}
                >
                  <input
                    type="radio"
                    name="letterType"
                    value={lt.type}
                    checked={letterType === lt.type}
                    onChange={(e) => setLetterType(e.target.value)}
                    className="sr-only"
                  />
                  <div style={{ color: letterType === lt.type ? 'var(--color-brand)' : 'var(--color-text-muted)' }}>
                    {getLetterTypeIcon(lt.type)}
                  </div>
                  <div>
                    <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{lt.name}</div>
                    <div className="cs-meta">{lt.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Case Information */}
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-4">Case Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="cs-label block mb-1">Insured Name *</label>
                <input
                  type="text"
                  value={caseData.insured_name}
                  onChange={(e) => handleInputChange('insured_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="John Smith"
                  data-testid="input-insured-name"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Business Name</label>
                <input
                  type="text"
                  value={caseData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="ABC Restaurant LLC"
                  data-testid="input-business-name"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Carrier Name *</label>
                <input
                  type="text"
                  value={caseData.carrier_name}
                  onChange={(e) => handleInputChange('carrier_name', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="State Farm Insurance"
                  data-testid="input-carrier-name"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">State *</label>
                <select
                  value={caseData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="select-state"
                >
                  {/* An empty `state` used to select the first option, so a
                      Texas case silently rendered "Alabama" — and this field
                      chooses which statutes the letter cites. A required,
                      legally load-bearing field must never default silently;
                      make "unset" an explicit, invalid choice. */}
                  <option value="">Select state…</option>
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
                {!caseData.state && (
                  <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                    Required — statutory references depend on it.
                  </p>
                )}
              </div>
              <div>
                <label className="cs-label block mb-1">Policy Number *</label>
                <input
                  type="text"
                  value={caseData.policy_number}
                  onChange={(e) => handleInputChange('policy_number', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="POL-123456"
                  data-testid="input-policy-number"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Claim Number *</label>
                <input
                  type="text"
                  value={caseData.claim_number}
                  onChange={(e) => handleInputChange('claim_number', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="CLM-789012"
                  data-testid="input-claim-number"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Claim Amount ($) *</label>
                <input
                  type="number"
                  value={caseData.claim_amount}
                  onChange={(e) => handleInputChange('claim_amount', parseFloat(e.target.value) || 0)}
                  className="cs-figure w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  placeholder="500000"
                  data-testid="input-claim-amount"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Event Date</label>
                <input
                  type="date"
                  value={caseData.event_date}
                  onChange={(e) => handleInputChange('event_date', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="input-event-date"
                />
              </div>
              <div>
                <label className="cs-label block mb-1">Event Type</label>
                <select
                  value={caseData.event_type}
                  onChange={(e) => handleInputChange('event_type', e.target.value)}
                  className="cs-body w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="select-event-type"
                >
                  <option value="fire">Fire</option>
                  <option value="flood">Flood</option>
                  <option value="hurricane">Hurricane</option>
                  <option value="tornado">Tornado</option>
                  <option value="theft">Theft</option>
                  <option value="vandalism">Vandalism</option>
                  <option value="water_damage">Water Damage</option>
                  <option value="wind">Wind</option>
                  <option value="hail">Hail</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="cs-label block mb-1">Response Deadline (Days)</label>
                <input
                  type="number"
                  value={responseDeadlineDays}
                  onChange={(e) => setResponseDeadlineDays(parseInt(e.target.value) || 30)}
                  min={7}
                  max={90}
                  className="cs-figure w-full px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                  data-testid="input-deadline-days"
                />
              </div>
            </div>

            {/* Advanced Fields Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="cs-body flex items-center gap-2 mt-4"
              style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
              data-testid="toggle-advanced"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Fields
            </button>

            {showAdvanced && (
              <div className="mt-4 pt-4 grid grid-cols-2 gap-4" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
                <div>
                  <label className="cs-label block mb-1">Attorney Name</label>
                  <input
                    type="text"
                    value={caseData.attorney_name}
                    onChange={(e) => handleInputChange('attorney_name', e.target.value)}
                    className="cs-body w-full px-3 py-2"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                    placeholder="Jane Doe, Esq."
                  />
                </div>
                <div>
                  <label className="cs-label block mb-1">Law Firm</label>
                  <input
                    type="text"
                    value={caseData.attorney_firm}
                    onChange={(e) => handleInputChange('attorney_firm', e.target.value)}
                    className="cs-body w-full px-3 py-2"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                    placeholder="Smith & Associates"
                  />
                </div>
                <div>
                  <label className="cs-label block mb-1">Adjuster Name</label>
                  <input
                    type="text"
                    value={caseData.adjuster_name}
                    onChange={(e) => handleInputChange('adjuster_name', e.target.value)}
                    className="cs-body w-full px-3 py-2"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                    placeholder="Claims Adjuster"
                  />
                </div>
                <div>
                  <label className="cs-label block mb-1">Loss Period (Days)</label>
                  <input
                    type="number"
                    value={caseData.loss_period_days}
                    onChange={(e) => handleInputChange('loss_period_days', parseInt(e.target.value) || 0)}
                    className="cs-figure w-full px-3 py-2"
                    style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-primary)' }}
                    placeholder="90"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-4">Letter Options</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeCalculations}
                  onChange={(e) => setIncludeCalculations(e.target.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--color-brand)' }}
                />
                <span className="cs-body">Include loss calculations section</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeBadFaithWarning}
                  onChange={(e) => setIncludeBadFaithWarning(e.target.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: 'var(--color-brand)' }}
                />
                <span className="cs-body">Include bad faith warning</span>
              </label>
            </div>
          </div>

          {/* State Statutes Info */}
          {stateStatutes && (
            <div style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: 16 }}>
              <h4 className="cs-body flex items-center gap-2 mb-2" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                <AlertTriangle className="w-4 h-4" />
                {caseData.state} Statutory References
              </h4>
              <div className="cs-body space-y-1">
                <p><strong>Unfair Practices:</strong> {stateStatutes.unfair_practices}</p>
                <p><strong>Bad Faith:</strong> {stateStatutes.bad_faith}</p>
                <p><strong>Interest Rate:</strong> {stateStatutes.interest_rate}</p>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={generateLetter}
            disabled={loading || !caseData.insured_name || !caseData.carrier_name}
            className="cs-body w-full py-3 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500, borderRadius: 10, opacity: (loading || !caseData.insured_name || !caseData.carrier_name) ? 0.5 : 1 }}
            data-testid="generate-letter-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                Generate Letter
              </>
            )}
          </button>

          {error && (
            <div className="cs-body" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, padding: 16, color: 'var(--color-needs-human-text)' }}>
              {error}
              <LetterheadGateNotice blockingPlaceholders={blockingPlaceholders} />
            </div>
          )}
        </div>

        {/* Right Column: Preview */}
        <div className="space-y-6">
          <div className="overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--color-border-sub)', background: 'var(--color-bg-elevated)' }}>
              <h3 className="cs-h3">Letter Preview</h3>
              {generatedLetter && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="cs-meta flex items-center gap-1 px-3 py-1.5"
                    style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-secondary)' }}
                    data-testid="copy-letter-btn"
                  >
                    {copied ? <Check className="w-4 h-4" style={{ color: 'var(--color-verified)' }} /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadAsText}
                    className="cs-meta flex items-center gap-1 px-3 py-1.5"
                    style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 10, color: 'var(--color-text-secondary)' }}
                    data-testid="download-txt-btn"
                  >
                    <Download className="w-4 h-4" />
                    TXT
                  </button>
                  <button
                    onClick={downloadAsPdf}
                    className="cs-meta flex items-center gap-1 px-3 py-1.5"
                    style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, color: 'var(--color-needs-human-text)' }}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  <button
                    onClick={downloadAsDocx}
                    className="cs-meta flex items-center gap-1 px-3 py-1.5"
                    style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, color: 'var(--color-needs-human-text)' }}
                    data-testid="download-docx-btn"
                  >
                    <Download className="w-4 h-4" />
                    DOCX
                  </button>
                  {caseId && (
                    <button
                      onClick={downloadTemplateDocx}
                      className="cs-meta flex items-center gap-1 px-3 py-1.5"
                      style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 10, color: 'var(--color-needs-human-text)' }}
                      data-testid="download-template-docx-btn"
                      title="Download from pre-built DOCX template"
                    >
                      <Download className="w-4 h-4" />
                      Template
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 max-h-[700px] overflow-y-auto">
              {generatedLetter ? (
                <pre className="cs-figure whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--color-text-secondary)' }} data-testid="letter-preview">
                  {generatedLetter}
                </pre>
              ) : (
                <div className="text-center py-16" style={{ color: 'var(--color-text-muted)' }}>
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="cs-body">Fill in the form and click "Generate Letter" to see the preview</p>
                </div>
              )}
            </div>
          </div>

          {/* Review Sections Alert */}
          {reviewSections.length > 0 && (
            <div style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: 16 }}>
              <h4 className="cs-body flex items-center gap-2 mb-3" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                <AlertTriangle className="w-5 h-5" />
                Attorney Review Required ({reviewSections.length} sections)
              </h4>
              <ul className="space-y-2">
                {reviewSections.map((section, idx) => (
                  <li key={idx} className="cs-body flex items-start gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                    <span className="mt-1" style={{ color: 'var(--color-brand)' }}>•</span>
                    <span>
                      <strong>{section.location || section.reason}</strong>
                      {section.priority && <span className="cs-chip ml-2 px-1.5 py-0.5 rounded" style={{ background: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>{section.priority}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </AttorneyLayout>
  );
}
